import { AutoTunePresentationSchema } from "@/pages/auto-tune/data/presentation";
import { z } from "zod";

// The cluster Auto Tune tab reuses the jobs status / history / savings
// schemas verbatim (the BE returns the identical shapes); only the
// recommendation list and the toggle request/response are
// cluster-specific. Re-export the shared ones so callers import from a
// single module.
export {
  AutoTuneStatusResponseSchema,
  AutoTuneHistoryResponseSchema,
  AutoTuneSavingsTrendsResponseSchema,
} from "@/pages/jobs/auto-tune/types";
export type {
  AutoTuneStatusResponse,
  AutoTuneHistoryResponse,
  AutoTuneHistoryEvent,
  AutoTuneSavingsTrendsResponse,
  AutoTuneSavingsTrendPoint,
} from "@/pages/jobs/auto-tune/types";

// The cluster recommendation actions exposed as drawer toggles.
// ``resize`` tightens the autoscaling minimum worker bound (Min Worker Node
// Sizing); ``auto_terminate`` lowers ``autotermination_minutes`` (Auto
// Termination); ``family_change`` swaps the worker/driver instance family
// (Right Sizing) — produced by ml-workflows' rules_rec engine, so its
// recommended_config carries worker/driver ``node_type`` rather than a
// single number.
export const ClusterRecTypeSchema = z.enum(["resize", "auto_terminate", "family_change"]);
export type ClusterRecType = z.infer<typeof ClusterRecTypeSchema>;

// The two recommendation kinds behind the single Autoscale toggle:
// ``resize`` steps the worker bounds down, ``autoscale`` enables autoscaling
// on a fixed-size cluster. Both arm the same axis flag and render as the
// Autoscale row; whichever the cluster has feeds the row (resize first).
export const CLUSTER_WORKER_BOUNDS_REC_TYPES = ["resize", "autoscale"] as const;
export const ClusterToggleRecTypeSchema = z.enum([
  "resize",
  "auto_terminate",
  "family_change",
  "autoscale",
]);
export type ClusterToggleRecType = z.infer<typeof ClusterToggleRecTypeSchema>;

// The axis a backend ``rec_type`` belongs to: ``autoscale`` rows live under
// the Autoscale (``resize``) axis; anything unknown maps to null.
export const recTypeToAxis = (recType: string | null | undefined): ClusterRecType | null => {
  if (recType === "autoscale") return "resize";
  const parsed = ClusterRecTypeSchema.safeParse(recType);
  return parsed.success ? parsed.data : null;
};

// The display label for a backend ``rec_type`` — its axis's label, or the
// caller's fallback for a rec_type that maps to no axis (e.g. "all").
export const axisLabelFor = (recType: string | null | undefined, fallback: string): string => {
  const axis = recTypeToAxis(recType);
  return axis ? CLUSTER_REC_TYPE_LABEL[axis] : fallback;
};

// The recommendation the Autoscale row shows and toggles: the cluster's
// ``resize`` rec when it has one, else its ``autoscale`` rec.
export const pickWorkerBoundsRec = <T extends { rec_type: string }>(
  recommendations: T[]
): T | undefined =>
  CLUSTER_WORKER_BOUNDS_REC_TYPES.map(kind =>
    recommendations.find(rec => rec.rec_type === kind)
  ).find(Boolean);

// The kind the Autoscale toggle sends for this cluster — ``resize`` when no
// worker-bounds recommendation is known (the axis can still be disarmed).
export const pickWorkerBoundsKind = (
  recommendations: { rec_type: string }[]
): ClusterToggleRecType =>
  ClusterToggleRecTypeSchema.safeParse(pickWorkerBoundsRec(recommendations)?.rec_type).data ??
  "resize";

// Single source of truth for the rec-type display labels. The drawer
// sub-toggle titles and the toggle toasts both read from here so the
// copy can never drift between "what the user clicked" and "what the
// confirmation says".
// "Min Worker Node Sizing" (not "Worker Sizing") — the drawer's copy
// contract names this axis for the min-worker leg specifically, since the
// max-worker leg has no server-side rec_type yet and renders as a separate
// inert "Coming soon" row (see ClusterAutoTuneSettingsDrawer's
// REC_TYPE_META). The Cost Savings tooltip leg keeps the shorter "Worker
// sizing" wording — both map back to this one server
// rec_type, they just read differently in a table row vs. a tooltip line.
export const CLUSTER_REC_TYPE_LABEL: Record<ClusterRecType, string> = {
  resize: "Autoscale",
  auto_terminate: "Auto Termination",
  family_change: "Right Sizing",
};

// The Cost Savings chart tooltip's shorter leg labels (DECISIONS.md's
// two-leg mapping) — distinct from ``CLUSTER_REC_TYPE_LABEL``'s drawer-row
// wording. Same backend ``rec_type``, just a terser label for a tooltip
// line: "Worker sizing" / "Auto termination", not "Min Worker Node
// Sizing" / "Auto Termination Minutes".
export const CLUSTER_REC_TYPE_TOOLTIP_LABEL: Record<ClusterRecType, string> = {
  resize: "Autoscale",
  auto_terminate: "Auto termination",
  family_change: "Right sizing",
};

// The configuration-row flag each toggleable rec_type flips — the keys the
// status response returns as ``axis_flags``. Mirrors the backend's
// ``CLUSTER_CONFIG_FLAG_BY_REC_TYPE``.
export const CLUSTER_TOGGLE_FLAG_KEY: Record<ClusterToggleRecType, string> = {
  resize: "resize_enabled",
  autoscale: "autoscale_enabled",
  auto_terminate: "auto_terminate_enabled",
  family_change: "family_change_enabled",
};

// The flags that arm each axis. The Autoscale axis has two: its toggle
// names whichever worker-bounds kind the cluster's rec carries, so the
// backend flips ``resize_enabled`` OR ``autoscale_enabled`` — reading one
// alone renders an armed (even applied) axis as off. The header pill and
// the settings drawer both size themselves off this map, so a cluster's
// Auto Tune state is always described in terms of the axes that exist,
// never the recommendations that happen to be active right now.
export const CLUSTER_AXIS_FLAG_KEYS: Record<ClusterRecType, readonly string[]> = {
  resize: CLUSTER_WORKER_BOUNDS_REC_TYPES.map(kind => CLUSTER_TOGGLE_FLAG_KEY[kind]),
  auto_terminate: [CLUSTER_TOGGLE_FLAG_KEY.auto_terminate],
  family_change: [CLUSTER_TOGGLE_FLAG_KEY.family_change],
};

export const CLUSTER_AXIS_REC_TYPES = Object.keys(CLUSTER_AXIS_FLAG_KEYS) as ClusterRecType[];

// An axis's position from ``axis_flags``: on when any of its flags is on;
// ``undefined`` when the response carries none of them (a backend that
// predates the field), so callers can fall back to the per-rec ``enabled``
// annotations.
export const clusterAxisFlagValue = (
  axis: ClusterRecType,
  axisFlags: Record<string, boolean> | null | undefined
): boolean | undefined => {
  const present = CLUSTER_AXIS_FLAG_KEYS[axis]
    .map(key => axisFlags?.[key])
    .filter((value): value is boolean => value !== undefined);
  return present.length === 0 ? undefined : present.some(Boolean);
};

export interface ClusterAxisEnablement {
  axesEnabled: number;
  axesTotal: number;
  enabledRecTypes: ClusterRecType[];
}

// Shared enablement derivation — the header pill (``ClusterAutoTuneControl``)
// and the Optimizations-enabled KPI card (``AutoTuneKpiCards``) both
// read this so "x/y enabled" and the subtitle's knob names can never drift
// apart. Prefers the config row's ``axis_flags`` (sees an armed axis even
// with no active recommendation); falls back to the per-rec ``enabled``
// annotations on backends that predate the field.
export const computeClusterAxisEnablement = (
  axisFlags: Record<string, boolean> | null | undefined,
  recommendations: { rec_type: string; enabled: boolean }[]
): ClusterAxisEnablement => {
  if (!axisFlags) {
    const enabledRecTypes = [
      ...new Set(
        recommendations
          .filter(rec => rec.enabled)
          .map(rec => recTypeToAxis(rec.rec_type))
          .filter((axis): axis is ClusterRecType => axis !== null)
      ),
    ];
    return {
      axesEnabled: enabledRecTypes.length,
      axesTotal: recommendations.length,
      enabledRecTypes,
    };
  }
  const enabledRecTypes = CLUSTER_AXIS_REC_TYPES.filter(
    recType => clusterAxisFlagValue(recType, axisFlags) === true
  );
  return {
    axesEnabled: enabledRecTypes.length,
    axesTotal: CLUSTER_AXIS_REC_TYPES.length,
    enabledRecTypes,
  };
};

// One all-purpose-cluster recommendation from ``databricks_recommendation``
// (``entity_type='all_purpose'``). ``enabled`` is the resolved sub-toggle
// state from the cluster's auto-tune configuration row.
export const ClusterAutoTuneRecommendationSchema = z.object({
  presentation: AutoTunePresentationSchema.optional(),
  change_description: z.string().optional(),
  entity_id: z.string(),
  entity_name: z.string().nullable().optional(),
  rec_type: z.string(),
  rec_source: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  owned_by: z.string().nullable().optional(),
  workspace_id: z.string().nullable().optional(),
  // SKU knobs — symmetric current vs recommended. ``resize`` carries
  // ``{min_workers, max_workers}``; ``auto_terminate`` carries
  // ``{auto_termination_minutes}``.
  current_config: z.record(z.string(), z.unknown()).default({}),
  recommended_config: z.record(z.string(), z.unknown()).default({}),
  current_cost_per_hr_dbu: z.number().nullable().optional(),
  recommended_cost_per_hr_dbu: z.number().nullable().optional(),
  estimated_savings_pct: z.number().nullable().optional(),
  recommendation_summary: z.string().nullable().optional(),
  reasoning: z.string().nullable().optional(),
  // Customer-safe explanation; null unless the producer marked it shareable.
  customer_reasoning: z.string().nullable().optional(),
  inference_start_date: z.string().nullable().optional(),
  inference_end_date: z.string().nullable().optional(),
  enabled: z.boolean().default(false),
  // Latest audit event on THIS recommendation's own chain (its
  // ``rec_type`` rows plus master-toggle/legacy rows). ``APPLIED``
  // moves the rec into the card's green Applied group; anything else
  // keeps it in the blue Proposed group.
  latest_action_event_type: z.string().nullable().optional(),
  // The value actually set on the cluster, in ``recommended_config``
  // vocabulary. Present only when the rec's chain is at ``APPLIED``;
  // the apply worker descends stepwise, so this can sit between the
  // default and the rec's final target. Absent → fall back to the
  // rec's target values.
  applied_config: z.record(z.string(), z.unknown()).nullable().optional(),
});

export type ClusterAutoTuneRecommendation = z.infer<typeof ClusterAutoTuneRecommendationSchema>;

export const ClusterAutoTuneRecommendationsResponseSchema = z.object({
  recommendations: z.array(ClusterAutoTuneRecommendationSchema).default([]),
  total_count: z.number().default(0),
});

export type ClusterAutoTuneRecommendationsResponse = z.infer<
  typeof ClusterAutoTuneRecommendationsResponseSchema
>;

export const ClusterAutoTuneTogglePreflightSchema = z.object({
  blocked: z.boolean().default(false),
  reason: z.string().nullable().optional(),
  actionable_message: z.string().nullable().optional(),
});

export const ClusterAutoTuneToggleResponseSchema = z.object({
  auto_tune_mode: z.string().nullable().optional(),
  audit_event_id: z.number().nullable().optional(),
  entity_name: z.string().nullable().optional(),
  preflight: ClusterAutoTuneTogglePreflightSchema.default({ blocked: false }),
  // rec_types whose disable physically reverted the live cluster to its
  // original configuration (vs a record-only flag flip). Optional so
  // older backends without the field degrade to the generic copy.
  rolled_back_rec_types: z.array(z.string()).optional(),
});

export type ClusterAutoTuneToggleResponse = z.infer<typeof ClusterAutoTuneToggleResponseSchema>;

// A backoff always rolls back to the last clean config. A stored legacy
// ``revert_to_original`` falls to "rollback" via the ``.catch`` below.
export const ClusterAutoTuneBackoffTriggerActionSchema = z.enum(["rollback"]);
export type ClusterAutoTuneBackoffTriggerAction = z.infer<
  typeof ClusterAutoTuneBackoffTriggerActionSchema
>;

// One global backoff policy for the whole cluster — thresholds, the
// cold-start signal, and which action a backoff takes. Server-defaulted
// (PG-overridable code constants); the drawer's card mirrors these
// defaults locally so a freshly-enabled cluster shows sane values before
// the user ever saves. ``.catch()`` (not ``.default()``) on every field —
// ``.default()`` only substitutes when a field is missing entirely; an
// explicit ``null`` (a real shape the BE can send) would otherwise parse
// through as null and render as the literal string "undefined" wherever
// the drawer displays it.
export const ClusterAutoTuneBackoffPolicySchema = z.object({
  // ONE threshold for every optimization: cold starts since apply as a
  // percent of the cluster's pre-tune rate (150 = 1.5× the rate in the
  // same-length window before the change; min 100).
  cold_start_threshold_pct: z.number().catch(150),
  trigger_action: ClusterAutoTuneBackoffTriggerActionSchema.catch("rollback"),
});
export type ClusterAutoTuneBackoffPolicy = z.infer<typeof ClusterAutoTuneBackoffPolicySchema>;

export const DEFAULT_CLUSTER_AUTO_TUNE_BACKOFF_POLICY: ClusterAutoTuneBackoffPolicy = {
  cold_start_threshold_pct: 150,
  trigger_action: "rollback",
};

// Notification config for the whole cluster — never a webhook URL inline;
// Slack channels are referenced by id (sourced from the tenant's Settings
// notification channels, never created from this drawer).
export const ClusterAutoTuneNotificationConfigSchema = z.object({
  notify_backoffs_and_failures: z.boolean().catch(true),
  notify_changes_applied: z.boolean().catch(false),
  slack_enabled: z.boolean().catch(false),
  slack_channel_ids: z.array(z.coerce.string()).catch([]),
  email_enabled: z.boolean().catch(false),
  email_recipients: z.array(z.string()).catch([]),
});
export type ClusterAutoTuneNotificationConfig = z.infer<
  typeof ClusterAutoTuneNotificationConfigSchema
>;

export const DEFAULT_CLUSTER_AUTO_TUNE_NOTIFICATION_CONFIG: ClusterAutoTuneNotificationConfig = {
  notify_backoffs_and_failures: true,
  notify_changes_applied: false,
  slack_enabled: false,
  slack_channel_ids: [],
  email_enabled: false,
  email_recipients: [],
};

export const ClusterAutoTuneConfigResponseSchema = z.object({
  // ``.catch()`` here too — a whole-object ``null`` (not just a missing
  // key) must still fall back to the full default object.
  backoff_policy: ClusterAutoTuneBackoffPolicySchema.catch(
    DEFAULT_CLUSTER_AUTO_TUNE_BACKOFF_POLICY
  ),
  notification_config: ClusterAutoTuneNotificationConfigSchema.catch(
    DEFAULT_CLUSTER_AUTO_TUNE_NOTIFICATION_CONFIG
  ),
  entity_type: z.string().nullable().optional(),
});
export type ClusterAutoTuneConfigResponse = z.infer<typeof ClusterAutoTuneConfigResponseSchema>;

// PATCH body — same shape minus ``entity_type`` (the API infers it from
// the route; only clusters wire a route to it in this ticket).
export interface ClusterAutoTuneConfigRequest {
  backoff_policy: ClusterAutoTuneBackoffPolicy;
  notification_config: ClusterAutoTuneNotificationConfig;
}

// Audit events for the Cost Savings chart's labeled vertical markers:
// cluster-level enable/disable transitions plus every monitor backoff
// and axis-level revert. Plain per-axis enable toggles draw no marker.
export const ClusterAutoTuneToggleEventSchema = z.object({
  event_type: z.enum(["enabled", "disabled"]),
  change_timestamp: z.string(),
  // The axis the toggle flipped — a ``ClusterRecType``, or "all" for a
  // master-toggle row that flipped every axis at once.
  rec_type: z.string().catch("all"),
});
export type ClusterAutoTuneToggleEvent = z.infer<typeof ClusterAutoTuneToggleEventSchema>;

export const ClusterAutoTuneToggleEventsResponseSchema = z.object({
  events: z.array(ClusterAutoTuneToggleEventSchema).default([]),
});
export type ClusterAutoTuneToggleEventsResponse = z.infer<
  typeof ClusterAutoTuneToggleEventsResponseSchema
>;

// One merged point for the redesigned Cost Savings chart, built client-side
// by merging a per-day cost series with the savings-trends ``actual``/
// ``potential`` series (see ``mergeCostSavingsSeries`` in
// ClusterAutoTuneSavingsCard.tsx). ``potentialLine``/``actualLine`` are the
// two boundary series the chart draws relative to ``cost`` — potential
// carved OUT of the cost (below it), actual drawn additively ON TOP of it.
// The by-rec-type maps back each total to its per-optimization legs for
// the hover tooltip, keyed by the entity's leg id — a cluster axis, a
// warehouse optimization (``auto_terminate`` / ``resize``) or a job
// task-run role (``worker_sizing`` / ``driver_sizing``).
export interface ClusterCostSavingsPoint {
  period: string;
  cost: number;
  potentialLine: number;
  actualLine: number;
  potentialTotal: number;
  actualTotal: number;
  potentialByRecType: Partial<Record<string, number>>;
  actualByRecType: Partial<Record<string, number>>;
}
